import type { TypeButtonLinkComponentWithoutUnresolvableLinksResponse } from './_generated';

export type ContentfulButtonLink = TypeButtonLinkComponentWithoutUnresolvableLinksResponse;

export const EXTERNAL_LINK_REL = 'noopener noreferrer';

export interface ContentfulTargetProps {
    target?: '_blank' | '_self' | '_top';
    rel?: string;
}

/**
 * Maps the Contentful `target` field onto the DOM.
 *
 * `_blank` also gets `rel="noopener noreferrer"`: a new tab without `rel` keeps
 * `window.opener`, so the opened page can rewrite this tab (tabnabbing) and sees
 * our Referer. `noreferrer` is the privacy half of the same pair.
 *
 * Anything else is omitted rather than forwarded: to a browser an unrecognised
 * target is a *window name*, so it would open the link in a stray window. With
 * no attribute the link just navigates here, same as `_self`. That also covers
 * the legacy `modal` value still present in the CMS enum (PPWEB-6338).
 */
export function resolveContentfulTargetProps(target?: string | null): ContentfulTargetProps {
    if (target === '_blank') {
        return { target: '_blank', rel: EXTERNAL_LINK_REL };
    }

    if (target === '_self' || target === '_top') {
        return { target };
    }

    return {};
}

export interface ContentfulLink {
    /** Spread onto `next/link`, `<a>` or any anchor-like component. */
    anchorProps: { href: string } & ContentfulTargetProps;
    /** Public CMS `title` only — never the editorial `name`. */
    label: string;
    entry: ContentfulButtonLink;
}

/** A `link` field (entries may be unresolved), or a single entry. */
type LinkSource = ContentfulButtonLink | undefined | readonly (ContentfulButtonLink | undefined)[];

/**
 * Every renderable entry in `source`, in authoring order.
 *
 * Entries without a destination, or without a public `title` (the editorial
 * `name` is not for display), are dropped instead of rendered as a dead anchor.
 */
export function resolveContentfulLinks(source: LinkSource): ContentfulLink[] {
    const entries: readonly (ContentfulButtonLink | undefined)[] = Array.isArray(source)
        ? source
        : [source];

    return entries.flatMap((entry) => {
        const { link, title, target } = entry?.fields ?? {};

        if (!entry || !link || !title) {
            return [];
        }

        return [
            {
                anchorProps: { href: link, ...resolveContentfulTargetProps(target) },
                label: title,
                entry,
            },
        ];
    });
}

/** First renderable entry in `source`, or `null`. */
export function resolveContentfulLink(source: LinkSource): ContentfulLink | null {
    return resolveContentfulLinks(source)[0] ?? null;
}
